import { useTranslation } from 'react-i18next';
import type { WorkspaceInvitation } from '@/types';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useFetchWorkspaceInvitations, useRevokeWorkspaceInvitation } from '@/hooksApi';
import { cn } from '@/lib/utils';

interface Props {
  workspaceUid: string;
}

/**
 * What this workspace has SENT — pending, and spent ones as history.
 *
 * ⭐ STATUS IS A WORD, NOT A COLOUR (addendum §12). A chip that distinguishes
 * "pending" from "expired" only by hue is unreadable to a colourblind merchant
 * and invisible in a screenshot pasted into a support thread.
 *
 * ⚠️ Expiry is a STATE, not a status: the server keeps an expired invitation at
 * `pending` and reports the timeout separately via `is_expired`. Reading
 * `status` alone would show a week-old dead invitation as though someone still
 * had it to accept.
 */
/**
 * How an invitation's roles are written.
 *
 * A role the merchant created gets THEIR name for it, verbatim — renaming
 * `finance` to something else is a decision they made and the UI must not
 * overrule it. Only our own shared preset gets a translated label, because
 * "admin" is a word we chose rather than a name they did.
 *
 * An EMPTY set means an invitation sent before access roles existed. The server
 * refuses to accept those, so it is named as needing a re-send rather than
 * given a plausible-looking label that would only mislead.
 */
const invitationRoleLabel = (
  roles: WorkspaceInvitation['roles'],
  t: (key: string) => unknown,
): string => {
  if (!roles || roles.length === 0) return String(t('workspace.role_unavailable'));

  return roles
    .map((role) => (role.is_default ? String(t('workspace.role_admin')) : role.name))
    .join(', ');
};

const SentInvitations = ({ workspaceUid }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchWorkspaceInvitations(workspaceUid);
  const revoke = useRevokeWorkspaceInvitation(workspaceUid);

  const invitations = data?.object ?? [];

  if (isLoading) {
    return (
      <div className='animate-pulse space-y-2 py-2'>
        <div className='h-4 w-1/2 rounded bg-sd-muted' />
        <div className='h-4 w-1/3 rounded bg-sd-muted' />
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <p className='py-3 text-[13px] text-sd-muted-foreground'>
        {String(t('workspace.no_sent_invitations'))}
      </p>
    );
  }

  const onRevoke = async (uid: string) => {
    try {
      await revoke.mutateAsync(uid);
      toast({ title: String(t('workspace.revoked')) });
    } catch {
      toast({ title: String(t('workspace.revoke_failed')), variant: 'destructive' });
    }
  };

  return (
    <ul className='divide-y divide-sd-border'>
      {invitations.map((invitation) => {
        const expired = invitation.is_expired;
        const pending = invitation.status === 'pending' && !expired;
        const statusKey = expired ? 'expired' : invitation.status;

        return (
          <li key={invitation.uid} className='flex items-center gap-3 py-2.5'>
            {/* QA U6 — the name is stored now, so show it. A list of bare
                phone numbers asks the merchant to recognise a colleague by
                digits; the name is the thing they actually know them by. Null
                on invitations sent before names were captured, and those still
                have to read correctly, so the phone stays as the identity and
                the name sits in front of it when there is one. */}
            <span className='flex min-w-0 flex-1 items-baseline gap-2 truncate'>
              {invitation.first_name || invitation.last_name ? (
                <bdi dir='auto' className='truncate text-[13px] text-sd-foreground'>
                  {[invitation.first_name, invitation.last_name].filter(Boolean).join(' ')}
                </bdi>
              ) : null}
              {/* bdi + dir=ltr: an Arabic paragraph would otherwise reorder the
                  digits of a phone number and show the merchant a number that
                  is not the one they typed. */}
              <bdi dir='ltr' className='truncate font-mono text-[12px] text-sd-muted-foreground'>
                {invitation.phone}
              </bdi>
            </span>

            <bdi dir='auto' className='shrink-0 text-[12px] text-sd-muted-foreground'>
              {invitationRoleLabel(invitation.roles, t)}
            </bdi>

            <span
              className={cn(
                'shrink-0 rounded-full border px-2 py-px text-[11px]',
                pending
                  ? 'border-sd-border text-sd-foreground'
                  : 'border-transparent bg-sd-muted text-sd-muted-foreground',
              )}
            >
              {String(t(`workspace.status_${statusKey}`))}
            </span>

            {pending && (
              <Button
                size='sm'
                variant='ghost'
                className='h-7 shrink-0 px-2 text-[12px]'
                disabled={revoke.isLoading}
                aria-label={`${String(t('workspace.revoke'))} — ${invitation.phone}`}
                onClick={() => void onRevoke(invitation.uid)}
              >
                {revoke.isLoading ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' aria-hidden />
                ) : (
                  String(t('workspace.revoke'))
                )}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default SentInvitations;
